import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaracteristicasDaPeca, Product, ProductApplication, ProductCode,
} from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  atualizarPeca, categoriasExistentes, criarPeca, tiposDePeca, criarSimilares, excluirPeca, fornecedores,
  sincronizarAplicacoesDoGrupo,
  fornecedoresDaPeca, limparPosicao, listarCaracteristicas, modelosCatalogo,
  adicionarFotoPeca, definirFotoPrincipal, fotosDaPeca, montadorasCatalogo,
  obterPecaParaEdicao, proximoSku, quemMoraNaPosicao, removerFoto,
  salvarFornecedoresDaPeca, similaresDaPeca, urlDaFoto, marcarFaxina, type NovaPeca,
} from '../lib/consultas';
import { colarAplicacoes } from '../lib/aplicacoes';
import { enderecoSemEstado, lerEstadoDaFaxina } from '../lib/faxina';
import { comprimirParaJpeg, type ImagemComprimida } from '../lib/imagem';
import { paraNumero, precoSugerido, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import { podeAdministrar, podeGerenciarCatalogo, useSessao } from '../lib/sessao';
import { sugerirTipo } from '../lib/tipoSugerido';

// Tela 4 — dois modos, uma tela (WEB_PLAN §2.5).
//   RÁPIDO: 6 campos. É o que torna a política "vendeu → cadastrou" viável no
//           balcão. O que falta fica pendente em cadastro_completo=false.
//   COMPLETO: abas com o resto. Ninguém preenche 25 campos com cliente esperando.
// Regra de ouro: cadastro pela metade é infinitamente melhor que peça fora do
// sistema — por isso o modo rápido nunca exige campo fiscal ou logístico.

type Aba = 'identificacao' | 'aplicacao' | 'estoque' | 'precos' | 'fiscal' | 'logistica' | 'foto';

interface Formulario {
  sku: string; descricao: string; categoria: string; marca: string; unidade: string;
  codigo_oem: string; codigo_fabrica: string; codigo_barras: string;
  codigo_barras_fabricante: string; posicao: string;
  ncm: string; origem_mercadoria: number;
  peso_kg: string; altura_cm: string; largura_cm: string; comprimento_cm: string;
  preco_custo: string; preco_balcao: string; preco_ecommerce: string;
  preco_promocional: string;
  /** Multiplicador DECIDIDO sobre o custo (029). Documentado na peça. */
  margem_alvo: string;
  observacoes: string; ativo: boolean;
  /** Tipo de peça (092) e a descrição explicativa (092). */
  tipo: string; detalhes: string;
  quantidade: string; local: string; estoque_min: string; estoque_max: string; reorder_point: string;
  codigos: Array<{ tipo: ProductCode['tipo']; valor: string }>;
  aplicacoes: Array<{
    montadora: string; modelo: string; ano_inicial: string; ano_final: string;
    motorizacao: string; observacao: string;
  }>;
  /** Características criadas pelo dono (migração 017), por attribute_id. */
  caracteristicas: CaracteristicasDaPeca;
  /** Similares (migração 027): cada linha vira uma peça própria no mesmo grupo. */
  similares: Array<{ marca: string; codigo: string; preco_balcao: string }>;
}

type AplicacaoForm = Formulario['aplicacoes'][number];

const VAZIO: Formulario = {
  sku: '', descricao: '', categoria: '', marca: '', unidade: 'un',
  codigo_oem: '', codigo_fabrica: '', codigo_barras: '', codigo_barras_fabricante: '',
  posicao: '',
  ncm: '', origem_mercadoria: 0,
  peso_kg: '', altura_cm: '', largura_cm: '', comprimento_cm: '',
  preco_custo: '', preco_balcao: '', preco_ecommerce: '',
  preco_promocional: '', margem_alvo: '',
  observacoes: '', ativo: true, tipo: '', detalhes: '',
  quantidade: '', local: '', estoque_min: '', estoque_max: '', reorder_point: '',
  codigos: [], aplicacoes: [], caracteristicas: {}, similares: [],
};

const ORIGENS = [
  [0, '0 — Nacional'], [1, '1 — Importação direta'], [2, '2 — Importação mercado interno'],
  [3, '3 — Nacional, importação 40%-70%'], [4, '4 — Nacional, processos produtivos básicos'],
  [5, '5 — Nacional, importação até 40%'], [6, '6 — Importação sem similar nacional'],
  [7, '7 — Importação mercado interno sem similar'], [8, '8 — Nacional, importação acima de 70%'],
] as const;

/** Um cadastro é "completo" quando dá para vender no balcão E anunciar online. */
function estaCompleto(f: Formulario): boolean {
  return Boolean(
    f.descricao.trim() && f.marca.trim() && f.categoria.trim()
    && (f.codigo_oem.trim() || f.codigo_fabrica.trim() || f.codigo_barras.trim()
      || f.codigo_barras_fabricante.trim())
    && paraNumero(f.preco_balcao) && f.local.trim()
    && f.ncm.trim().length === 8 && paraNumero(f.peso_kg)
    && f.aplicacoes.some((a) => a.montadora.trim() && a.modelo.trim()),
  );
}

export default function Cadastro() {
  const { id } = useParams();
  const editando = Boolean(id);
  const navigate = useNavigate();
  const cliente = useQueryClient();
  const { papel } = useSessao();

  const [modo, setModo] = useState<'rapido' | 'completo'>(editando ? 'completo' : 'rapido');
  const [aba, setAba] = useState<Aba>('identificacao');
  // "Nada encontrado" da busca manda o código bipado junto (state do router):
  // a peça nova já nasce com o código preenchido, pronto para o próximo bip.
  const local = useLocation();
  const codigoBipado = (local.state as { codigo?: string } | null)?.codigo;
  // A LISTA DE FAXINA (120) manda mais: o nome que o vendedor digitou, o código
  // do balcão, o carro e a CHAVE da linha de origem — é ela que faz o item sair
  // da lista quando a peça nascer. Leitor puro, no padrão de
  // lerEstadoDaEntrada: o estado do roteador pode vir de qualquer lugar.
  // Capturado UMA vez, de propósito: o efeito abaixo limpa o estado do
  // histórico, e `chave` é preciso mais tarde, na hora de salvar. Lido a cada
  // render, ele teria desaparecido antes do Salvar e o item nunca sairia da
  // lista.
  const [daFaxina] = useState(() => lerEstadoDaFaxina(local.state));
  const [f, setF] = useState<Formulario>(() => {
    if (editando) return VAZIO;
    // Peça NOVA já nasce com a margem padrão da loja (a última usada): a
    // prática é "1,8× a 2,0× para qualquer produto", e o registro por peça é o
    // que o dono quer acumular para estudar margem × oferta/demanda.
    return {
      ...VAZIO,
      margem_alvo: margemPadrao(),
      ...(codigoBipado ? { codigo_fabrica: codigoBipado } : {}),
      ...(daFaxina.descricao ? { descricao: daFaxina.descricao } : {}),
      ...(daFaxina.codigo ? { codigo_fabrica: daFaxina.codigo } : {}),
      // O carro vira a primeira APLICAÇÃO, que é onde ele mora no cadastro —
      // e é o campo que o catálogo 092 exige para a peça aparecer por carro.
      ...(daFaxina.montadora || daFaxina.modelo
        ? {
          aplicacoes: [{
            montadora: daFaxina.montadora, modelo: daFaxina.modelo,
            ano_inicial: '', ano_final: '', motorizacao: '', observacao: '',
          }],
        }
        : {}),
    };
  });

  // O estado do roteador é de UMA leitura. Sem isto, quem cadastra a peça, sai
  // da tela e volta (o "voltar" do Android, ou recarregar) encontra o
  // formulário preenchido DE NOVO com o mesmo nome — e um toque em Salvar
  // cadastra a mesma peça duas vezes. Foi exatamente o bug da compra na hora
  // (revisão do #273); o banco não tem trava de repetição.
  useEffect(() => {
    if (local.state === null || local.state === undefined) return;
    navigate(enderecoSemEstado(local), { replace: true, state: null });
  }, [local, navigate]);

  // obterPecaParaEdicao e não obterPeca: custo e preço de e-commerce foram
  // revogados da leitura direta de products (migração 014) e vêm por RPC. A
  // chave de cache é própria para não colidir com a ficha, que não traz custo.
  const { data: existente, isLoading } = useQuery({
    queryKey: ['peca-edicao', id],
    queryFn: () => obterPecaParaEdicao(id!),
    enabled: editando,
  });

  const { data: tipos } = useQuery({ queryKey: ['tipos-de-peca'], queryFn: tiposDePeca, staleTime: 10 * 60_000 });
  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: categoriasExistentes,
    staleTime: 10 * 60_000,
  });

  // As características que o dono criou (migração 017). Só as ativas: é esta
  // lista que decide QUAIS campos o formulário mostra. Os VALORES vêm de
  // `existente`, num efeito separado — não há corrida entre as duas consultas
  // porque elas alimentam coisas diferentes (a armadilha do useEffect que apaga
  // o que o usuário digitou, no CLAUDE.md, é quando as duas mexem no mesmo
  // estado).
  const caracteristicas = useQuery({
    queryKey: ['caracteristicas', 'ativas'],
    queryFn: () => listarCaracteristicas(true),
    staleTime: 10 * 60_000,
  });

  // Montadoras do catálogo FIPE (vehicle_catalog) para o autocomplete da aba
  // Aplicação. staleTime longo de propósito: o catálogo muda uma vez por mês
  // (fipe-sync), não durante um cadastro.
  const montadoras = useQuery({
    queryKey: ['catalogo', 'montadoras'],
    queryFn: montadorasCatalogo,
    staleTime: 24 * 60 * 60_000,
  });

  useEffect(() => {
    if (!existente) return;
    const { produto: p, estoque, codigos, aplicacoes, caracteristicas } = existente;
    const txt = (v: unknown) => (v === null || v === undefined ? '' : String(v));
    setF({
      sku: p.sku, descricao: p.descricao, categoria: txt(p.categoria), marca: txt(p.marca),
      unidade: p.unidade, codigo_oem: txt(p.codigo_oem), codigo_fabrica: txt(p.codigo_fabrica),
      codigo_barras: txt(p.codigo_barras),
      codigo_barras_fabricante: txt(p.codigo_barras_fabricante),
      posicao: txt(p.posicao), ncm: txt(p.ncm),
      origem_mercadoria: p.origem_mercadoria,
      peso_kg: txt(p.peso_kg), altura_cm: txt(p.altura_cm), largura_cm: txt(p.largura_cm),
      comprimento_cm: txt(p.comprimento_cm), preco_custo: txt(p.preco_custo),
      preco_balcao: txt(p.preco_balcao), preco_ecommerce: txt(p.preco_ecommerce),
      preco_promocional: txt(p.preco_promocional),
      margem_alvo: txt(p.margem_alvo).replace('.', ','),
      observacoes: txt(p.observacoes), ativo: p.ativo,
      tipo: txt(p.tipo), detalhes: txt(p.detalhes),
      quantidade: txt(estoque?.quantidade), local: txt(estoque?.local),
      estoque_min: txt(estoque?.estoque_min), estoque_max: txt(estoque?.estoque_max),
      reorder_point: txt(estoque?.reorder_point),
      // Sempre vazio ao carregar: no editar as linhas são ACRÉSCIMOS ao grupo,
      // nunca o espelho do que existe (as criadas aparecem na ficha).
      similares: [],
      codigos: codigos.map((c) => ({ tipo: c.tipo, valor: c.valor })),
      aplicacoes: aplicacoes.map((a: ProductApplication) => ({
        montadora: a.montadora, modelo: a.modelo,
        ano_inicial: txt(a.ano_inicial), ano_final: txt(a.ano_final),
        motorizacao: txt(a.motorizacao), observacao: txt(a.observacao),
      })),
      caracteristicas: { ...caracteristicas },
    });
  }, [existente]);

  const set = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) =>
    setF((atual) => ({ ...atual, [campo]: valor }));

  const completo = useMemo(() => estaCompleto(f), [f]);

  // O tipo pelo começo do nome (ADR-009), quando a pessoa não escolheu um: sem
  // ele, a peça do modo Rápido sumia de "Por tipo" e "Por carro" (revisão do
  // #254). Aparece na tela e vale no salvar da peça NOVA. Não vale: na edição
  // (salvar o preço de uma peça antiga não troca o tipo dela sem ninguém ver),
  // nem quando a pessoa escolheu "Sem tipo" de propósito (2ª revisão).
  const [semTipoDeProposito, setSemTipoDeProposito] = useState(false);
  const tipoDoNome = useMemo(() => {
    if (f.tipo) return null;
    const slug = sugerirTipo(f.descricao, tipos ?? []);
    return (tipos ?? []).find((t) => t.slug === slug) ?? null;
  }, [f.tipo, f.descricao, tipos]);
  const tipoPeloNome = editando || semTipoDeProposito ? null : tipoDoNome;

  const montarPayload = (): NovaPeca => {
    const num = (v: string) => paraNumero(v);
    const txt = (v: string) => (v.trim() ? v.trim() : null);

    const produto: Partial<Product> & { sku: string; descricao: string } = {
      sku: f.sku.trim(),
      descricao: f.descricao.trim(),
      categoria: txt(f.categoria) ?? tipoPeloNome?.categoria ?? null,
      marca: txt(f.marca),
      unidade: f.unidade.trim() || 'un',
      codigo_oem: txt(f.codigo_oem),
      codigo_fabrica: txt(f.codigo_fabrica),
      codigo_barras: txt(f.codigo_barras),
      codigo_barras_fabricante: txt(f.codigo_barras_fabricante),
      posicao: txt(f.posicao),
      ncm: txt(f.ncm),
      origem_mercadoria: f.origem_mercadoria,
      peso_kg: num(f.peso_kg),
      altura_cm: num(f.altura_cm),
      largura_cm: num(f.largura_cm),
      comprimento_cm: num(f.comprimento_cm),
      preco_balcao: num(f.preco_balcao),
      preco_ecommerce: num(f.preco_ecommerce),
      preco_promocional: num(f.preco_promocional),
      observacoes: txt(f.observacoes),
      tipo: txt(f.tipo) ?? tipoPeloNome?.slug ?? null,
      detalhes: txt(f.detalhes),
      ativo: f.ativo,
      cadastro_completo: completo,
    };
    // Custo e margem alvo são dado sensível — gerente e dono editam (decisão
    // do dono, 11/08/2026: o gerente também recebe mercadoria e precifica).
    // Quem garante de verdade é o banco: a coluna é revogada de leitura para
    // os demais, e a RPC de leitura checa o papel.
    if (podeGerenciarCatalogo(papel)) {
      produto.preco_custo = num(f.preco_custo);
      produto.margem_alvo = num(f.margem_alvo);
    }

    const estoque: NovaPeca['estoque'] = {};
    if (num(f.quantidade) !== null) estoque.quantidade = num(f.quantidade)!;
    if (txt(f.local)) estoque.local = txt(f.local);
    if (num(f.estoque_min) !== null) estoque.estoque_min = num(f.estoque_min)!;
    if (num(f.estoque_max) !== null) estoque.estoque_max = num(f.estoque_max)!;
    if (num(f.reorder_point) !== null) estoque.reorder_point = num(f.reorder_point)!;

    return {
      produto,
      estoque: Object.keys(estoque).length ? estoque : undefined,
      codigos: f.codigos.filter((c) => c.valor.trim()),
      aplicacoes: f.aplicacoes
        .filter((a) => a.montadora.trim() && a.modelo.trim())
        .map((a) => ({
          montadora: a.montadora.trim(), modelo: a.modelo.trim(),
          ano_inicial: paraNumero(a.ano_inicial), ano_final: paraNumero(a.ano_final),
          motorizacao: a.motorizacao.trim() || null, observacao: a.observacao.trim() || null,
        })),
      // Só as características ATIVAS entram no payload. Sem este recorte, uma
      // característica desativada depois de preenchida seria apagada da peça no
      // primeiro salvamento — o campo não aparece na tela, o valor não está em
      // `f.caracteristicas`, e o que não vem seria tratado como esvaziado.
      caracteristicas: Object.fromEntries(
        (caracteristicas.data ?? []).map((c) => [c.id, f.caracteristicas[c.id] ?? []]),
      ),
    };
  };

  // Quem costuma ter esta peça. Vive fora de `f` porque não é campo de
  // `products`: é a ponte supplier_products, gravada depois que a peça existe
  // (uma peça nova só tem id DEPOIS do insert).
  const [fornecedoresPeca, setFornecedoresPeca] = useState<VinculoFornecedor[]>([]);

  // As fotos de uma peça NOVA: comprimidas na hora da escolha, mas só sobem
  // depois do salvar — não existe product_id antes do insert. Sobem NA ORDEM
  // da lista (a primeira vira a principal, como na galeria da 031). Na edição
  // a aba grava direto e este estado nem é usado.
  const [fotosPendentes, setFotosPendentes] = useState<ImagemComprimida[]>([]);

  const vinculosSalvos = useQuery({
    queryKey: ['fornecedores-da-peca', id],
    queryFn: () => fornecedoresDaPeca(id!),
    enabled: Boolean(editando && id && podeGerenciarCatalogo(papel)),
  });

  // Trava de "tocado": sem ela, a consulta respondendo tarde apaga o que a
  // pessoa acabou de escolher. É a armadilha da folha, documentada no CLAUDE.md.
  const [vinculosCarregados, setVinculosCarregados] = useState(false);
  useEffect(() => {
    if (vinculosCarregados || !vinculosSalvos.data) return;
    setFornecedoresPeca(vinculosSalvos.data.map((v) => ({
      supplier_id: v.supplier_id, codigo_fornecedor: v.codigo_fornecedor ?? '',
    })));
    setVinculosCarregados(true);
  }, [vinculosSalvos.data, vinculosCarregados]);

  const salvar = useMutation({
    mutationFn: async () => {
      if (!f.descricao.trim()) throw new Error('O nome da peça é obrigatório.');
      // A mesma régua do check products_promocao_valida, dita ANTES do erro de
      // constraint: promoção exige balcão preenchido e desconto de verdade.
      const promo = paraNumero(f.preco_promocional);
      if (promo !== null) {
        const balcao = paraNumero(f.preco_balcao);
        if (promo <= 0 || balcao === null || promo >= balcao) {
          throw new Error('Preço promocional precisa ser maior que zero e MENOR que o preço '
            + 'de balcão. Para tirar a peça da promoção, deixe o campo vazio.');
        }
      }
      // Similares validam ANTES de criar a peça: erro aqui não pode deixar a
      // base gravada e o grupo pela metade.
      const linhasSimilares = f.similares
        .filter((s) => s.marca.trim() || s.codigo.trim() || s.preco_balcao.trim());
      if (linhasSimilares.some((s) => !s.marca.trim())) {
        throw new Error('Toda similar precisa de marca — preencha ou remova a linha.');
      }

      const payload = montarPayload();
      if (!payload.produto.sku) {
        payload.produto.sku = await proximoSku(payload.produto.categoria || f.marca || 'PEC');
      }
      const produto = editando ? await atualizarPeca(id!, payload) : await criarPeca(payload);

      // Só depois da peça, porque uma peça nova só ganha id agora. Falhar aqui
      // não pode derrubar o cadastro que a pessoa acabou de digitar — a peça já
      // está gravada. Mas também não pode falhar em silêncio: o aviso viaja no
      // RESULTADO (e não num setState daqui, que a onSuccess não enxergaria a
      // tempo) e segura a navegação para a pessoa ler.
      let avisoFornecedores: string | null = null;
      // A trava que faltava e custou o vínculo do dono em 10/08/2026: se a
      // lista não CARREGOU, gravá-la é apagar o que existe no banco. Editar
      // "nome da peça" não pode ter como efeito colateral desvincular
      // fornecedor. Só grava quem tem certeza do que está na tela.
      const listaConfiavel = !editando || vinculosCarregados;
      if (podeGerenciarCatalogo(papel) && listaConfiavel) {
        try {
          await salvarFornecedoresDaPeca(produto.id, fornecedoresPeca.map((v) => ({
            supplier_id: v.supplier_id,
            codigo_fornecedor: v.codigo_fornecedor.trim() || null,
          })));
        } catch (e) {
          avisoFornecedores = mensagemDeErro(e);
        }
        // As fotos pendentes sobem DEPOIS da peça, pelo mesmo motivo dos
        // fornecedores: agora existe id. Em SÉRIE e na ordem da lista — a
        // primeira que entra é a principal (posição, não flag). Falhar no meio
        // não derruba o cadastro: a peça está salva, e o aviso diz quantas
        // subiram para as outras irem pela edição.
        if (!editando && fotosPendentes.length > 0) {
          let subiram = 0;
          try {
            for (const foto of fotosPendentes) {
              await adicionarFotoPeca({
                productId: produto.id,
                base64: foto.base64,
                largura: foto.largura,
                altura: foto.altura,
              });
              subiram += 1;
            }
          } catch (e) {
            avisoFornecedores = avisoFornecedores
              ?? `${subiram} de ${fotosPendentes.length} foto(s) subiram `
                + `(${mensagemDeErro(e)}) — abra a peça para acrescentar as outras`;
          }
        }
      } else if (podeGerenciarCatalogo(papel) && editando) {
        avisoFornecedores = 'a lista de fornecedores não chegou a carregar, '
          + 'então não mexi nela — o que já estava vinculado continua lá.';
      }

      // As variantes de marca, depois da base pelo mesmo motivo dos
      // fornecedores: a peça já existe e não pode ser perdida se isto falhar.
      // A RPC é transacional — ou o grupo inteiro, ou nada. No EDITAR as linhas
      // ACRESCENTAM marcas ao grupo (a RPC reaproveita o grupo_similar da base)
      // — é também a rota de recuperação quando isto falha no criar.
      let avisoSimilares: string | null = null;
      if (linhasSimilares.length) {
        try {
          await criarSimilares(
            produto.id,
            linhasSimilares.map((s) => ({
              marca: s.marca.trim(),
              codigo: s.codigo.trim() || null,
              preco_balcao: paraNumero(s.preco_balcao),
            })),
            podeGerenciarCatalogo(papel)
              ? fornecedoresPeca.map((v) => v.supplier_id)
              : [],
          );
        } catch (e) {
          avisoSimilares = mensagemDeErro(e);
        }
      }

      // A aplicação é do GRUPO (13/08/2026): salvar a edição de um membro
      // replica as aplicações dele nas outras marcas — é o caminho de volta
      // para grupo que nasceu sem aplicação. A RPC é no-op sem grupo ou com a
      // lista vazia (abrir a similar só para acertar preço não apaga nada).
      if (editando && produto.grupo_similar && (payload.aplicacoes?.length ?? 0) > 0) {
        try {
          await sincronizarAplicacoesDoGrupo(produto.id);
        } catch (e) {
          avisoSimilares = avisoSimilares
            ?? `as aplicações não replicaram nas similares (${mensagemDeErro(e)})`;
        }
      }
      // A LISTA DE FAXINA (120) FECHA O CICLO AQUI. Vindo dela, a peça nasceu
      // para resolver um texto que o balcão digitou — e marcar 'cadastrada' é o
      // que faz aquele item SAIR da lista. Sem isto a lista nunca encolhe, e
      // uma lista que não encolhe deixa de ser usada.
      //
      // DEPOIS da peça e tolerante a falha, pelo mesmo motivo dos fornecedores
      // e das fotos: a peça já está gravada, e perder a marcação custa uma
      // linha a mais na lista — derrubar o cadastro custaria o trabalho todo.
      // Só no CRIAR: editar uma peça que já existe não resolve texto nenhum.
      let avisoFaxina: string | null = null;
      if (!editando && daFaxina.chave) {
        try {
          await marcarFaxina({
            chave: daFaxina.chave,
            situacao: 'cadastrada',
            descricao: daFaxina.descricao || f.descricao.trim(),
            productId: produto.id,
          });
        } catch (e) {
          avisoFaxina = `a peça foi salva, mas ela continua na lista do que falta `
            + `cadastrar (${mensagemDeErro(e)}) — use "Isto não é peça" lá, ou `
            + `cadastre de novo pelo botão da lista`;
        }
      }

      return {
        produto,
        avisoFornecedores: avisoFornecedores ?? avisoFaxina,
        avisoSimilares,
      };
    },
    onSuccess: ({ produto, avisoFornecedores, avisoSimilares }) => {
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['peca', produto.id] });
      cliente.invalidateQueries({ queryKey: ['categorias'] });
      cliente.invalidateQueries({ queryKey: ['fornecedores-da-peca', produto.id] });
      cliente.invalidateQueries({ queryKey: ['similares'] });
      // A lista de faxina (120) muda com QUALQUER peça nova, não só com a que
      // veio dela: a coluna "já existe parecido no catálogo" é calculada contra
      // o catálogo inteiro. Por isso invalida sempre, sem olhar daFaxina.chave.
      cliente.invalidateQueries({ queryKey: ['faxina-familias'] });
      cliente.invalidateQueries({ queryKey: ['faxina-itens'] });
      if (!avisoFornecedores && !avisoSimilares) navigate(`/peca/${produto.id}`);
    },
  });

  if (editando && isLoading) return <Layout titulo="Editar peça"><Carregando /></Layout>;

  const gerarSku = async () => {
    const sugestao = await proximoSku(f.categoria || f.marca || 'PEC');
    set('sku', sugestao);
  };

  return (
    <Layout titulo={editando ? 'Editar peça' : 'Nova peça'}>
      <form
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); salvar.mutate(); }}
        onKeyDown={(e) => {
          // O leitor USB "digita" o código e finaliza com Enter — e Enter em
          // <input> submete o formulário: era o bipe cadastrando a peça pela
          // metade, só com nome e código (visto no balcão em 07/08/2026).
          // Aqui cadastrar é SÓ no botão. O Enter segue valendo no textarea
          // (quebra de linha) e no próprio botão de salvar (tagName BUTTON).
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
      >
        {/* Vendedor só vê o rápido: o just-in-time é criar a peça em 60s no
            meio do atendimento; o completo (fiscal, logística, custo) é
            trabalho de gerência — mesma régua da RLS (rls.sql §1.1). */}
        {!editando && podeGerenciarCatalogo(papel) && (
          <div className="flex gap-2">
            {(['rapido', 'completo'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setModo(m)}
                aria-pressed={modo === m}
                className={[
                  'flex-1 border px-4 py-3 text-sm font-bold',
                  modo === m ? 'border-mute bg-edge text-paper' : 'border-edge text-mute',
                ].join(' ')}
              >
                {m === 'rapido' ? 'Rápido (60s)' : 'Completo'}
              </button>
            ))}
          </div>
        )}

        {modo === 'rapido' ? (
          <section className="cartao space-y-4 p-4">
            <div>
              <Campo rotulo="Nome da peça *" valor={f.descricao} onChange={(v) => set('descricao', v)}
                     placeholder="Pastilha de freio dianteira" autoFocus />
              {tipoPeloNome && (
                <p className="mt-1 text-xs text-dim">
                  Tipo: {tipoPeloNome.categoria} · {tipoPeloNome.nome} (pelo nome — troque no Completo)
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Marca" valor={f.marca} onChange={(v) => set('marca', v)} placeholder="Fras-le" />
              <Campo rotulo="Categoria" valor={f.categoria} onChange={(v) => set('categoria', v)}
                     placeholder="Freios" lista={categorias} />
            </div>
            <Campo
              rotulo="Código (fabricante, OEM ou barras)"
              valor={f.codigo_fabrica}
              onChange={(v) => set('codigo_fabrica', v)}
              placeholder="Pode bipar o código de barras"
              mono
            />
            {/* Custo e margem são dado sensível: gerência (gerente e dono) vê
                e edita — quem recebe mercadoria cadastra com o catálogo do
                fornecedor aberto, que é quando o custo está na frente. */}
            {podeGerenciarCatalogo(papel) && (
              <CustoComMargem
                custo={f.preco_custo}
                margem={f.margem_alvo}
                onCusto={(v) => set('preco_custo', v)}
                onMargem={(v) => set('margem_alvo', v)}
                aoUsar={(p) => set('preco_balcao', p)}
              />
            )}
            <div className="grid gap-4 sm:grid-cols-3">
              <Campo rotulo="Preço balcão (R$)" valor={f.preco_balcao} onChange={(v) => set('preco_balcao', v)}
                     placeholder="0,00" numerico />
              <div>
                <Campo rotulo="Localização" valor={f.local} onChange={(v) => set('local', v)}
                       placeholder="A-P3-G2" mono />
                <AvisoDePosicao local={f.local} ignorarId={id} />
              </div>
              <Campo rotulo="Quantidade" valor={f.quantidade} onChange={(v) => set('quantidade', v)}
                     placeholder="0" numerico />
            </div>
            <Repetidor
              titulo="Similares (outras marcas)"
              ajuda="Cada linha vira uma peça própria — mesma descrição, marca e código diferentes — já ligada a esta como similar. A ficha mostra o grupo inteiro: quando uma marca falta, a alternativa aparece."
              itens={f.similares}
              novo={() => ({ marca: '', codigo: '', preco_balcao: '' })}
              onChange={(v) => set('similares', v)}
              render={(item, alterar) => <LinhaSimilar item={item} alterar={alterar} />}
            />
            <p className="text-xs text-dim">
              O resto (fiscal, dimensões, aplicações) pode ser completado depois — a peça já fica
              buscável na hora. O SKU é gerado automaticamente.
            </p>
          </section>
        ) : (
          <>
            <nav className="flex flex-wrap gap-1" aria-label="Seções do cadastro">
              {([
                ['identificacao', 'Identificação'], ['aplicacao', 'Aplicação'], ['estoque', 'Estoque'],
                ['precos', 'Preços'], ['fiscal', 'Fiscal'], ['logistica', 'Logística'],
                ['foto', 'Foto'],
              ] as const).map(([chave, rotulo]) => (
                <button
                  key={chave}
                  type="button"
                  onClick={() => setAba(chave)}
                  aria-pressed={aba === chave}
                  className={[
                    'border px-3 py-2 text-xs font-bold',
                    aba === chave ? 'border-signal text-signal' : 'border-edge text-dim',
                  ].join(' ')}
                >
                  {rotulo}
                </button>
              ))}
            </nav>

            <section className="cartao space-y-4 p-4">
              {aba === 'identificacao' && (
                <>
                  <Campo rotulo="Nome da peça *" valor={f.descricao} onChange={(v) => set('descricao', v)} autoFocus />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="rotulo" htmlFor="sku">Código interno (SKU)</label>
                      <div className="flex gap-2">
                        <input id="sku" className="campo font-mono" value={f.sku}
                               onChange={(e) => set('sku', e.target.value)} placeholder="FRE-0001" />
                        <button type="button" className="botao-secundario px-3" onClick={gerarSku}>
                          Gerar
                        </button>
                      </div>
                    </div>
                    <Campo rotulo="Marca" valor={f.marca} onChange={(v) => set('marca', v)} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Campo rotulo="Categoria" valor={f.categoria} onChange={(v) => set('categoria', v)} lista={categorias} />
                    <Campo rotulo="Posição / lado" valor={f.posicao} onChange={(v) => set('posicao', v)}
                           placeholder="dianteira, esquerdo…" />
                  </div>
                  {/* O TIPO é por onde o balcão navega ("Calotas → Fiat → Palio",
                      ADR-009); escolher o tipo preenche a categoria do grupo. */}
                  <div>
                    <label className="rotulo" htmlFor="tipo-peca">Tipo de peça</label>
                    <select id="tipo-peca" className="campo" value={f.tipo}
                            onChange={(e) => {
                              const t = (tipos ?? []).find((x) => x.slug === e.target.value);
                              setSemTipoDeProposito(e.target.value === '');
                              setF((atual) => ({ ...atual, tipo: e.target.value, categoria: t ? t.categoria : atual.categoria }));
                            }}>
                      <option value="">Sem tipo</option>
                      {(tipos ?? []).map((t) => <option key={t.slug} value={t.slug}>{t.categoria} · {t.nome}</option>)}
                    </select>
                    {tipoPeloNome && (
                      <p className="mt-1 text-xs text-dim">
                        Pelo nome, vai salvar como {tipoPeloNome.categoria} · {tipoPeloNome.nome}. Peça sem tipo não
                        aparece em "Por tipo" nem em "Por carro".
                      </p>
                    )}
                    {!tipoPeloNome && tipoDoNome && (
                      <p className="mt-1 text-xs text-dim">
                        Pelo nome, o tipo seria {tipoDoNome.categoria} · {tipoDoNome.nome} — escolha acima para gravar.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="rotulo" htmlFor="detalhes">Descrição (para que serve)</label>
                    <textarea id="detalhes" className="campo min-h-20" value={f.detalhes} maxLength={800}
                              onChange={(e) => set('detalhes', e.target.value)}
                              placeholder="Em uma ou duas frases: para que serve e como se usa. É o que o balcão explica ao cliente." />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Campo rotulo="Código original (OEM)" valor={f.codigo_oem} onChange={(v) => set('codigo_oem', v)} mono />
                    <Campo rotulo="Código do fabricante" valor={f.codigo_fabrica} onChange={(v) => set('codigo_fabrica', v)} mono />
                    <Campo rotulo="Código de barras (EAN)" valor={f.codigo_barras} onChange={(v) => set('codigo_barras', v)} mono />
                    {/* O 4º identificador (032): a etiqueta do fabricante traz
                        o código do produto (SCT-02 → campo "fabricante") E um
                        barras próprio, que não é o EAN. Bipável como os irmãos. */}
                    <Campo rotulo="Cód. de barras do fabricante" valor={f.codigo_barras_fabricante}
                           onChange={(v) => set('codigo_barras_fabricante', v)} mono
                           ajuda="O número comprido da etiqueta do fabricante — não é o EAN. Pode bipar." />
                  </div>
                  <Campo rotulo="Unidade" valor={f.unidade} onChange={(v) => set('unidade', v)} placeholder="un, par, jogo, litro" />

                  {/* Características criadas pelo dono (migração 017). O bloco
                      some quando não há nenhuma — cadastro rápido no balcão não
                      pode ganhar um título vazio no meio. */}
                  {(caracteristicas.data ?? []).length > 0 && (
                    <div className="border-t border-edge pt-4">
                      <h3 className="rotulo mb-1">Características</h3>
                      <p className="mb-3 text-xs text-dim">
                        Campos criados pela loja. São o que permite filtrar a busca
                        depois (“só o lado direito”).
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {(caracteristicas.data ?? []).map((c) => {
                          // Multivalor (036): a peça pode ter VÁRIOS valores da
                          // mesma característica — o cabo que serve no motor
                          // 1.0 E no 1.3. Lista vira chips de marcar/desmarcar;
                          // texto e número viram chips + campo de acrescentar.
                          const valores = f.caracteristicas[c.id] ?? [];
                          const alternar = (v: string) =>
                            set('caracteristicas', {
                              ...f.caracteristicas,
                              [c.id]: valores.includes(v)
                                ? valores.filter((x) => x !== v)
                                : [...valores, v],
                            });

                          if (c.tipo === 'lista') {
                            // Valor gravado antes de a opção sair da lista
                            // continua visível e desmarcável, senão salvar
                            // apagaria em silêncio o que já estava na peça.
                            const opcoes = [
                              ...c.opcoes,
                              ...valores.filter((v) => !c.opcoes.includes(v)),
                            ];
                            return (
                              <div key={c.id}>
                                <p className="rotulo">{c.nome}</p>
                                <div className="flex flex-wrap gap-2">
                                  {opcoes.map((o) => (
                                    <button
                                      key={o}
                                      type="button"
                                      onClick={() => alternar(o)}
                                      aria-pressed={valores.includes(o)}
                                      className={`border px-2 py-1 text-xs ${
                                        valores.includes(o)
                                          ? 'border-signal bg-signal/10 font-bold'
                                          : 'border-edge text-mute'
                                      }`}
                                    >
                                      {o}{!c.opcoes.includes(o) ? ' (fora da lista)' : ''}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <CampoMultivalor
                              key={c.id}
                              nome={c.nome}
                              valores={valores}
                              numerico={c.tipo === 'numero'}
                              onAlternar={alternar}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Repetidor
                    titulo="Códigos equivalentes (cross-reference)"
                    ajuda="Cada código que um cliente ou fornecedor pode citar. É o que faz a busca achar a peça pelo código 'errado'."
                    itens={f.codigos}
                    novo={() => ({ tipo: 'cross' as const, valor: '' })}
                    onChange={(v) => set('codigos', v)}
                    render={(item, alterar) => (
                      <div className="grid grid-cols-[7rem_1fr] gap-2">
                        <select className="campo" value={item.tipo}
                                onChange={(e) => alterar({ ...item, tipo: e.target.value as ProductCode['tipo'] })}>
                          <option value="oem">OEM</option>
                          <option value="fabricante">Fabricante</option>
                          <option value="ean">EAN</option>
                          <option value="cross">Equivalente</option>
                        </select>
                        <input className="campo font-mono" value={item.valor} placeholder="Código"
                               onChange={(e) => alterar({ ...item, valor: e.target.value })} />
                      </div>
                    )}
                  />

                  {podeGerenciarCatalogo(papel) && (
                    <QuemTemAPeca
                      escolhidos={fornecedoresPeca}
                      onChange={setFornecedoresPeca}
                    />
                  )}

                  <Repetidor
                    titulo={editando ? 'Acrescentar similares (outras marcas)' : 'Similares (outras marcas)'}
                    ajuda={(editando
                      ? 'Cada linha vira uma peça NOVA no grupo desta — as que já existem estão na ficha. '
                      : 'Cada linha vira uma peça própria no mesmo grupo — herda descrição, aplicações e características; muda marca, código e preço. ')
                      + 'O código da linha também é gravado nos fornecedores escolhidos acima (é o caso do catálogo da Skypeças: um código por marca).'}
                    itens={f.similares}
                    novo={() => ({ marca: '', codigo: '', preco_balcao: '' })}
                    onChange={(v) => set('similares', v)}
                    render={(item, alterar) => <LinhaSimilar item={item} alterar={alterar} />}
                  />

                  <div>
                    <label className="rotulo" htmlFor="obs">Observações do balcão</label>
                    <textarea
                      id="obs" className="campo min-h-24" value={f.observacoes}
                      onChange={(e) => set('observacoes', e.target.value)}
                      placeholder="Macetes, equivalências que só o Serginho sabe, cuidados na aplicação…"
                    />
                    <p className="mt-1 text-xs text-dim">
                      Este campo é o mais valioso do sistema: é onde o conhecimento que hoje está só
                      na cabeça do Serginho vira dado consultável.
                    </p>
                  </div>
                </>
              )}

              {aba === 'aplicacao' && (
                <>
                  <ColarDoFornecedor
                    aoAdicionar={(linhas) => {
                      // Dedup contra o que já está na tela: colar a mesma tabela
                      // duas vezes não pode dobrar a lista.
                      const chaves = new Set(f.aplicacoes.map((a) => JSON.stringify(a)));
                      const novas = linhas.filter((l) => !chaves.has(JSON.stringify(l)));
                      if (novas.length) set('aplicacoes', [...f.aplicacoes, ...novas]);
                      return novas.length;
                    }}
                  />
                  <Repetidor
                    titulo="Veículos em que a peça serve"
                    ajuda="É o fitment. Alimenta a busca por veículo ('celta 2012') e, depois, o anúncio no Mercado Livre. Montadora e modelo sugerem do catálogo FIPE — escolher da lista evita 'VW'/'Volkswagen' virarem duas montadoras."
                    itens={f.aplicacoes}
                    novo={() => ({ montadora: '', modelo: '', ano_inicial: '', ano_final: '', motorizacao: '', observacao: '' })}
                    onChange={(v) => set('aplicacoes', v)}
                    render={(item, alterar) => (
                      <LinhaAplicacao item={item} alterar={alterar} montadoras={montadoras.data ?? []} />
                    )}
                  />
                </>
              )}

              {aba === 'estoque' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Campo rotulo="Localização na loja" valor={f.local} onChange={(v) => set('local', v)}
                           placeholder="A-P3-G2" mono
                           ajuda="Padrão corredor-prateleira-gaveta (decisão D7)." />
                    <AvisoDePosicao local={f.local} ignorarId={id} />
                  </div>
                  <Campo rotulo="Quantidade" valor={f.quantidade} onChange={(v) => set('quantidade', v)} numerico
                         ajuda={editando ? 'Alterar aqui grava direto. Para inventário, use "Ajustar estoque" na ficha.' : undefined} />
                  <Campo rotulo="Estoque mínimo" valor={f.estoque_min} onChange={(v) => set('estoque_min', v)} numerico />
                  <Campo rotulo="Ponto de pedido" valor={f.reorder_point} onChange={(v) => set('reorder_point', v)} numerico
                         ajuda="Abaixo disso a peça aparece no relatório de reposição." />
                  <Campo rotulo="Estoque máximo" valor={f.estoque_max} onChange={(v) => set('estoque_max', v)} numerico />
                </div>
              )}

              {aba === 'precos' && (
                <div className="grid gap-4 sm:grid-cols-3">
                  {podeGerenciarCatalogo(papel) && (
                    <div className="sm:col-span-3">
                      <CustoComMargem
                        custo={f.preco_custo}
                        margem={f.margem_alvo}
                        onCusto={(v) => set('preco_custo', v)}
                        onMargem={(v) => set('margem_alvo', v)}
                        aoUsar={(p) => set('preco_balcao', p)}
                      />
                    </div>
                  )}
                  <Campo rotulo="Preço balcão (R$)" valor={f.preco_balcao} onChange={(v) => set('preco_balcao', v)} numerico />
                  <Campo rotulo="Preço e-commerce (R$)" valor={f.preco_ecommerce} onChange={(v) => set('preco_ecommerce', v)} numerico />
                  <Campo
                    rotulo="Preço promocional (R$)"
                    valor={f.preco_promocional}
                    onChange={(v) => set('preco_promocional', v)}
                    numerico
                    ajuda='Preenchido, a peça entra na aba Promoções e na busca com o balcão rasurado, este preço em destaque e o % de desconto calculado sozinho. Precisa ser menor que o balcão; vazio = sem promoção.'
                  />
                  <p className="text-xs text-dim sm:col-span-3">
                    A margem não é digitada: ela é calculada pela RPC <span className="font-mono">margens</span>.
                    Guardar margem em coluna é garantir que ela vai dessincronizar do preço.
                    O % de desconto da promoção segue a mesma regra — deriva dos dois preços.
                  </p>
                </div>
              )}

              {aba === 'fiscal' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Campo rotulo="NCM (8 dígitos)" valor={f.ncm} onChange={(v) => set('ncm', v.replace(/\D/g, '').slice(0, 8))}
                         mono placeholder="87083090"
                         ajuda="Confirmar com o contador antes de emitir nota (decisão D11)." />
                  <div>
                    <label className="rotulo" htmlFor="origem">Origem da mercadoria</label>
                    <select id="origem" className="campo" value={f.origem_mercadoria}
                            onChange={(e) => set('origem_mercadoria', Number(e.target.value))}>
                      {ORIGENS.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {aba === 'logistica' && (
                <div className="grid gap-4 sm:grid-cols-4">
                  <Campo rotulo="Peso (kg)" valor={f.peso_kg} onChange={(v) => set('peso_kg', v)} numerico />
                  <Campo rotulo="Altura (cm)" valor={f.altura_cm} onChange={(v) => set('altura_cm', v)} numerico />
                  <Campo rotulo="Largura (cm)" valor={f.largura_cm} onChange={(v) => set('largura_cm', v)} numerico />
                  <Campo rotulo="Comprimento (cm)" valor={f.comprimento_cm} onChange={(v) => set('comprimento_cm', v)} numerico />
                  <p className="text-xs text-dim sm:col-span-4">
                    Peso e dimensões são o que o Mercado Livre pede para calcular frete (Fase 4).
                  </p>
                </div>
              )}

              {aba === 'foto' && (
                <AbaFoto
                  productId={editando ? id! : null}
                  grupoSimilar={editando ? (existente?.produto.grupo_similar ?? null) : null}
                  pendentes={fotosPendentes}
                  onPendentes={setFotosPendentes}
                />
              )}
            </section>
          </>
        )}

        <div className="cartao flex flex-wrap items-center justify-between gap-3 p-4">
          <p className="text-xs">
            <span className={completo ? 'text-ok' : 'text-alerta'}>
              {completo ? '✓ Cadastro completo' : '○ Cadastro incompleto'}
            </span>
            <span className="ml-2 text-dim">
              {completo
                ? 'Pronto para anunciar online.'
                : 'Suficiente para vender no balcão; completar depois.'}
            </span>
          </p>
          {editando && (
            <label className="flex items-center gap-2 text-xs text-mute">
              <input type="checkbox" checked={f.ativo} onChange={(e) => set('ativo', e.target.checked)} />
              Peça ativa
            </label>
          )}
        </div>

        {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
        {salvar.data?.avisoFornecedores && (
          <Aviso tipo="alerta">
            <p>
              <strong>A peça foi salva.</strong> O que não gravou foi a lista de fornecedores
              ({salvar.data.avisoFornecedores}). Abra a ficha e tente de novo — o resto do
              cadastro está seguro.
            </p>
            <Link to={`/peca/${salvar.data.produto.id}`} className="botao-secundario mt-2 inline-block">
              Ir para a peça
            </Link>
          </Aviso>
        )}
        {salvar.data?.avisoSimilares && (
          <Aviso tipo="alerta">
            <p>
              <strong>A peça foi salva.</strong> O que não gravou foram as similares
              ({salvar.data.avisoSimilares}). Nenhuma variante foi criada. NÃO salve este
              formulário de novo — isso duplicaria a peça. Abra a ficha, toque em Editar e
              acrescente as similares por lá.
            </p>
            <Link to={`/peca/${salvar.data.produto.id}`} className="botao-secundario mt-2 inline-block">
              Ir para a peça
            </Link>
          </Aviso>
        )}

        <div className="flex gap-2">
          <button type="submit" className="botao-primario flex-1 py-4" disabled={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : editando ? 'Salvar alterações' : 'Cadastrar peça'}
          </button>
          <button type="button" className="botao-secundario" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        </div>

        {/* Excluir: só ao EDITAR e só para o dono. A RLS e a RPC negam de
            verdade (peca_admin.sql); aqui a tela só esconde o que não cabe. */}
        {editando && podeAdministrar(papel) && (
          <ExcluirPeca
            id={id!}
            sku={f.sku}
            descricao={f.descricao}
            aoExcluir={() => {
              cliente.invalidateQueries({ queryKey: ['buscar'] });
              cliente.invalidateQueries({ queryKey: ['estoque'] });
              navigate('/', { replace: true });
            }}
          />
        )}
      </form>
    </Layout>
  );
}

/**
 * Zona de exclusão. Fica no fim da tela de edição, atrás de duas portas: o botão
 * revela a confirmação, e a confirmação exige digitar o SKU.
 *
 * Digitar o SKU parece exagero e não é: a lista de busca e esta tela se parecem
 * no celular, e apagar a peça errada é irreversível. O banco recusa apagar peça
 * COM histórico — o risco real é justamente a peça nova, sem venda, que alguém
 * acabou de cadastrar e some sem deixar rastro.
 */
function ExcluirPeca({ id, sku, descricao, aoExcluir }: {
  id: string; sku: string; descricao: string; aoExcluir: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');

  const excluir = useMutation({
    mutationFn: () => excluirPeca(id),
    onSuccess: aoExcluir,
  });

  if (!aberto) {
    return (
      <div className="mt-8 border-t border-edge pt-4">
        <button
          type="button"
          className="text-sm text-erro underline"
          onClick={() => setAberto(true)}
        >
          Excluir esta peça do catálogo
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 border border-erro/50 bg-erro/5 p-4">
      <h3 className="font-semibold text-erro">Excluir {sku}</h3>
      <p className="mt-1 text-sm text-mute">{descricao}</p>
      <p className="mt-2 text-sm text-mute">
        Peça <strong className="text-paper">com venda ou movimento de estoque não pode
        ser excluída</strong> — o histórico da loja depende dela, e o sistema vai
        recusar. Nesse caso, desmarque “Peça ativa” lá em cima: ela some da busca
        e do balcão, e o histórico fica preservado.
      </p>

      <label className="rotulo mt-4 block" htmlFor="confirmar-exclusao">
        Digite <span className="font-mono text-paper">{sku}</span> para confirmar
      </label>
      <input
        id="confirmar-exclusao"
        className="campo font-mono"
        value={confirmacao}
        onChange={(e) => setConfirmacao(e.target.value)}
        autoComplete="off"
      />

      {excluir.error ? (
        <div className="mt-3"><Aviso tipo="erro">{mensagemDeErro(excluir.error)}</Aviso></div>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="botao-secundario flex-1"
          onClick={() => { setAberto(false); setConfirmacao(''); }}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="flex-1 border border-erro bg-erro px-4 py-2 font-semibold text-ink disabled:opacity-40"
          disabled={confirmacao.trim() !== sku || excluir.isPending}
          onClick={() => excluir.mutate()}
        >
          {excluir.isPending ? 'Excluindo…' : 'Excluir de vez'}
        </button>
      </div>
    </div>
  );
}

/**
 * Linha da aba Aplicação com autocomplete do catálogo FIPE (decisão D15):
 * digitar "vol" sugere "Volkswagen", escolher a montadora carrega os modelos
 * dela. É datalist, não <select>, de propósito — o catálogo não cobre tudo
 * (utilitário antigo, linha agrícola), e texto livre continua valendo; a
 * sugestão só torna o caminho normalizado o mais curto.
 *
 * É componente próprio (e não JSX no render do Repetidor) porque a lista de
 * modelos é um useQuery POR LINHA, e hook não pode viver dentro de callback.
 */
function LinhaAplicacao({ item, alterar, montadoras }: {
  item: AplicacaoForm; alterar: (v: AplicacaoForm) => void; montadoras: string[];
}) {
  const uid = useId();
  const { data: modelos } = useQuery({
    queryKey: ['catalogo', 'modelos', item.montadora],
    queryFn: () => modelosCatalogo(item.montadora),
    enabled: item.montadora.trim().length > 0,
    staleTime: 24 * 60 * 60_000,
  });

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <div>
        <input className="campo" placeholder="Montadora (Chevrolet)" value={item.montadora}
               list={`${uid}-montadoras`} autoComplete="off"
               onChange={(e) => alterar({ ...item, montadora: e.target.value })} />
        <datalist id={`${uid}-montadoras`}>
          {montadoras.map((m) => <option key={m} value={m} />)}
        </datalist>
      </div>
      <div>
        <input className="campo" placeholder="Modelo (Celta)" value={item.modelo}
               list={`${uid}-modelos`} autoComplete="off"
               onChange={(e) => alterar({ ...item, modelo: e.target.value })} />
        <datalist id={`${uid}-modelos`}>
          {(modelos ?? []).map((m) => <option key={m} value={m} />)}
        </datalist>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="campo" inputMode="numeric" placeholder="Ano inicial" value={item.ano_inicial}
               onChange={(e) => alterar({ ...item, ano_inicial: e.target.value })} />
        <input className="campo" inputMode="numeric" placeholder="Ano final" value={item.ano_final}
               onChange={(e) => alterar({ ...item, ano_final: e.target.value })} />
      </div>
      <input className="campo" placeholder="Motorização (1.0 8V)" value={item.motorizacao}
             onChange={(e) => alterar({ ...item, motorizacao: e.target.value })} />
    </div>
  );
}

const CHAVE_MARGEM = 'serginho.margem-cadastro';

/** A margem padrão do próximo cadastro: a última usada, ou 1,9× de fábrica. */
function margemPadrao(): string {
  try { return localStorage.getItem(CHAVE_MARGEM) ?? '1,9'; } catch { return '1,9'; }
}

/**
 * Custo do fornecedor + margem alvo → balcão sugerido. Gerência (gerente e
 * dono) vê e edita.
 *
 * A margem digitada aqui É GRAVADA NA PEÇA (products.margem_alvo, migração
 * 029): é a decisão de precificação que o dono quer documentada por produto
 * para estudar margem × oferta/demanda. Não confundir com a margem REAL —
 * balcão÷custo —, que continua derivada dos preços (regra 4) e aparece na
 * ficha ao lado desta. O localStorage guarda só o PADRÃO do próximo cadastro.
 *
 * O balcão é preenchido por BOTÃO, nunca por efeito: preencher sozinho enquanto
 * se digita apagaria um preço já ajustado à mão — a armadilha do formulário
 * documentada no CLAUDE.md, só que em outra roupa.
 */
function CustoComMargem({ custo, margem, onCusto, onMargem, aoUsar }: {
  custo: string; margem: string;
  onCusto: (v: string) => void; onMargem: (v: string) => void;
  aoUsar: (preco: string) => void;
}) {
  const sugestao = precoSugerido(custo, margem);

  const mudarMargem = (v: string) => {
    onMargem(v);
    try { localStorage.setItem(CHAVE_MARGEM, v); } catch { /* navegação privada */ }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Campo rotulo="Preço de custo (R$)" valor={custo} onChange={onCusto} numerico
             ajuda="O que o fornecedor cobra." />
      <Campo rotulo="Margem alvo (×)" valor={margem} onChange={mudarMargem} numerico
             ajuda="A loja usa 1,8× a 2,0×. Fica gravada NA PEÇA e vira o padrão do próximo cadastro." />
      <div>
        <p className="rotulo">Balcão sugerido</p>
        <button
          type="button"
          className="botao-secundario w-full py-3"
          disabled={!sugestao}
          onClick={() => sugestao && aoUsar(sugestao)}
        >
          {sugestao ? `Usar R$ ${sugestao}` : '—'}
        </button>
        <p className="mt-1 text-xs text-dim">
          Custo × margem. Toca, o preço de balcão recebe o valor, e dá para ajustar à mão.
        </p>
      </div>
    </div>
  );
}

/**
 * A tabela de aplicação do fornecedor, colada de uma vez (lib/aplicacoes.ts).
 *
 * O fluxo que isto substitui: catálogo da Skypeças aberto num monitor, sistema
 * no outro, e alguém redigitando montadora/veículo/ano/motor linha por linha —
 * a razão de a aba Aplicação ficar vazia na prática. Aqui é Ctrl+C na tabela
 * do site, Ctrl+V no campo, e as linhas caem no Repetidor de baixo JÁ EDITÁVEIS:
 * o parser é tolerante e a conferência é da pessoa, com tudo na tela.
 */
function ColarDoFornecedor({ aoAdicionar }: {
  aoAdicionar: (linhas: AplicacaoForm[]) => number;
}) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState('');
  const [resumo, setResumo] = useState<string | null>(null);

  if (!aberto) {
    return (
      <div>
        <button type="button" className="botao-secundario w-full" onClick={() => setAberto(true)}>
          Colar tabela de aplicação do fornecedor
        </button>
        {resumo && <p className="mt-1 text-xs text-ok">{resumo}</p>}
      </div>
    );
  }

  const converter = () => {
    const { aplicacoes, ignoradas } = colarAplicacoes(texto);
    const novas = aoAdicionar(aplicacoes);
    const partes = [
      `${novas} veículo(s) adicionados`,
      aplicacoes.length - novas > 0 ? `${aplicacoes.length - novas} já estavam na lista` : '',
      ignoradas > 0 ? `${ignoradas} linha(s) não entendidas` : '',
    ].filter(Boolean);
    setResumo(`${partes.join(' · ')}. Confira as linhas abaixo — dá para editar cada uma.`);
    setTexto('');
    setAberto(false);
  };

  return (
    <div className="border border-edge p-3">
      <p className="rotulo">Colar tabela de aplicação do fornecedor</p>
      <p className="mb-2 text-xs text-dim">
        No site do fornecedor, selecione a tabela de aplicação inteira (com o cabeçalho),
        copie e cole aqui. Cada linha vira um veículo na lista de baixo.
      </p>
      <textarea
        className="campo min-h-32 font-mono text-xs"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={'Montadora\tVeículo\tMês/Ano\tModelo\tMotor…\nFIAT\tPALIO\t2000 até …\tWEEKEND\t1.0…'}
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="botao-primario flex-1"
          disabled={!texto.trim()}
          onClick={converter}
        >
          Converter em veículos
        </button>
        <button
          type="button"
          className="botao-secundario"
          onClick={() => { setAberto(false); setTexto(''); }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

/**
 * Linha da seção Similares: marca, código e preço opcional. O código é UM só de
 * propósito — no catálogo do fornecedor cada marca aparece com um código, e é
 * ele que identifica a variante (vai em codigo_fabrica e no vínculo com os
 * fornecedores escolhidos). Preço vazio = definir depois, na ficha da variante.
 */
function LinhaSimilar({ item, alterar }: {
  item: { marca: string; codigo: string; preco_balcao: string };
  alterar: (v: { marca: string; codigo: string; preco_balcao: string }) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_8rem]">
      <input className="campo" placeholder="Marca (NGK)" value={item.marca}
             autoComplete="off"
             onChange={(e) => alterar({ ...item, marca: e.target.value })} />
      <input className="campo font-mono" placeholder="Código" value={item.codigo}
             autoComplete="off"
             onChange={(e) => alterar({ ...item, codigo: e.target.value })} />
      <input className="campo" inputMode="decimal" placeholder="Preço (R$)" value={item.preco_balcao}
             onChange={(e) => alterar({ ...item, preco_balcao: e.target.value })} />
    </div>
  );
}

// --- Campos ------------------------------------------------------------------

function Campo({
  rotulo, valor, onChange, placeholder, mono, numerico, autoFocus, lista, ajuda,
}: {
  rotulo: string; valor: string; onChange: (v: string) => void; placeholder?: string;
  mono?: boolean; numerico?: boolean; autoFocus?: boolean; lista?: string[]; ajuda?: string;
}) {
  const id = `campo-${rotulo.replace(/\W+/g, '-').toLowerCase()}`;
  const listaId = lista?.length ? `${id}-lista` : undefined;
  return (
    <div>
      <label className="rotulo" htmlFor={id}>{rotulo}</label>
      <input
        id={id}
        className={`campo ${mono ? 'font-mono' : ''}`}
        value={valor}
        placeholder={placeholder}
        inputMode={numerico ? 'decimal' : undefined}
        autoFocus={autoFocus}
        autoComplete="off"
        list={listaId}
        onChange={(e) => onChange(e.target.value)}
      />
      {listaId && (
        <datalist id={listaId}>
          {lista!.map((o) => <option key={o} value={o} />)}
        </datalist>
      )}
      {ajuda && <p className="mt-1 text-xs text-dim">{ajuda}</p>}
    </div>
  );
}

function Repetidor<T>({
  titulo, ajuda, itens, novo, onChange, render,
}: {
  titulo: string; ajuda?: string; itens: T[]; novo: () => T;
  onChange: (v: T[]) => void; render: (item: T, alterar: (v: T) => void) => React.ReactNode;
}) {
  return (
    <div>
      <p className="rotulo">{titulo}</p>
      {ajuda && <p className="-mt-1 mb-2 text-xs text-dim">{ajuda}</p>}
      <ul className="space-y-2">
        {itens.map((item, i) => (
          // Índice como key é aceitável aqui: a lista é editada por posição e
          // não é reordenada — não existe identidade estável antes de salvar.
          <li key={i} className="flex items-start gap-2">
            <div className="flex-1">
              {render(item, (v) => onChange(itens.map((x, j) => (j === i ? v : x))))}
            </div>
            <button
              type="button"
              className="border border-edge px-3 py-3 text-sm text-erro"
              onClick={() => onChange(itens.filter((_, j) => j !== i))}
              aria-label="Remover linha"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="botao-secundario mt-2 w-full" onClick={() => onChange([...itens, novo()])}>
        + Adicionar
      </button>
    </div>
  );
}

export interface VinculoFornecedor {
  supplier_id: string;
  /** Código da peça no catálogo DELE — é o que se digita no site do fornecedor. */
  codigo_fornecedor: string;
}

/**
 * Quem costuma ter esta peça.
 *
 * Pedido do dono (10/08/2026): "disco de freio — fornecedor: MGA, Skypeças",
 * para saber onde procurar quando a peça acaba. A ponte já existia
 * (`supplier_products`) e era preenchida sozinha pela Entrada de mercadoria, com
 * o custo da compra; o que faltava era DECLARAR o vínculo antes de comprar.
 *
 * O código do fornecedor é o campo que faz isso valer a pena: é ele que se cola
 * na busca do site ou se manda no WhatsApp. Sem ele, "a MGA tem" ainda obriga
 * alguém a procurar a peça pelo nome no catálogo do outro.
 *
 * Custo NÃO aparece aqui, nem para o dono: cotação se registra comprando, na
 * Entrada de mercadoria, com data. Número digitado à mão numa tela de cadastro
 * envelhece sem ninguém perceber e vira margem errada seis meses depois.
 */
function QuemTemAPeca({ escolhidos, onChange }: {
  escolhidos: VinculoFornecedor[];
  onChange: (v: VinculoFornecedor[]) => void;
}) {
  const lista = useQuery({
    queryKey: ['fornecedores'],
    queryFn: fornecedores,
    staleTime: 5 * 60_000,
  });

  const disponiveis = lista.data ?? [];
  // Só os que ainda não estão na peça. Um select que oferece o que já foi
  // escolhido convida ao clique que não faz nada.
  const restantes = disponiveis.filter(
    (f) => !escolhidos.some((e) => e.supplier_id === f.id),
  );

  const nomeDe = (id: string) => disponiveis.find((f) => f.id === id)?.nome ?? 'fornecedor';

  const acrescentar = (supplierId: string) => {
    if (!supplierId) return;
    onChange([...escolhidos, { supplier_id: supplierId, codigo_fornecedor: '' }]);
  };

  const remover = (supplierId: string) =>
    onChange(escolhidos.filter((e) => e.supplier_id !== supplierId));

  const mudarCodigo = (supplierId: string, codigo: string) =>
    onChange(escolhidos.map((e) => (
      e.supplier_id === supplierId ? { ...e, codigo_fornecedor: codigo } : e)));

  return (
    <div>
      <p className="rotulo">Quem costuma ter esta peça</p>
      <p className="mb-2 text-xs text-mute">
        Escolha os fornecedores para saber onde procurar quando ela acabar. Eles aparecem
        no resultado da busca e na ficha, com link para o site de cada um.
      </p>

      {disponiveis.length === 0 ? (
        <p className="text-xs text-dim">
          Nenhum fornecedor cadastrado ainda. Cadastre em <Link to="/fornecedores"
          className="underline">Fornecedores</Link> e eles aparecem nesta lista.
        </p>
      ) : (
        <>
          {escolhidos.length > 0 && (
            <ul className="mb-2 space-y-2">
              {escolhidos.map((e) => (
                <li key={e.supplier_id} className="border border-edge p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{nomeDe(e.supplier_id)}</span>
                    <button type="button" onClick={() => remover(e.supplier_id)}
                            className="shrink-0 px-2 py-1 text-xs font-bold text-erro">
                      remover
                    </button>
                  </div>
                  {/* O código DELE é o que faz o link valer a pena: é o que se
                      cola na busca do site do fornecedor. O nosso SKU não
                      significa nada no catálogo do outro. */}
                  <input
                    className="campo mt-2 font-mono"
                    value={e.codigo_fornecedor}
                    placeholder={`Código da peça na ${nomeDe(e.supplier_id)} (opcional)`}
                    onChange={(ev) => mudarCodigo(e.supplier_id, ev.target.value)}
                  />
                </li>
              ))}
            </ul>
          )}

          {restantes.length > 0 ? (
            <select
              className="campo"
              value=""
              onChange={(ev) => acrescentar(ev.target.value)}
            >
              <option value="">
                {escolhidos.length === 0 ? 'Escolher fornecedor…' : 'Acrescentar outro…'}
              </option>
              {restantes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}{f.cidade ? ` — ${f.cidade}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-xs text-dim">Todos os fornecedores já estão nesta peça.</p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Quem o sistema acha que mora na posição digitada.
 *
 * A posição é da PRATELEIRA, não da peça (decisão do dono, 11/08/2026): calotas
 * X zeraram e saíram de linha, calotas Z herdam a parede. A troca sempre foi
 * possível — o que faltava era enxergar o morador antigo na hora de gravar,
 * para a ficha da linha morta não continuar mandando gente ao lugar errado.
 *
 * NÃO BLOQUEIA nada: gaveta dividida entre SKUs pequenos é legítima. O aviso
 * informa; o botão "liberar" (só gerência, e só para quem está zerada) tira a
 * peça antiga da posição com um toque — quem nega de verdade é a RLS de stock.
 */
function AvisoDePosicao({ local, ignorarId }: { local: string; ignorarId?: string }) {
  const { papel } = useSessao();
  const cliente = useQueryClient();
  const [termo, setTermo] = useState('');

  // Debounce: consultar a cada tecla seria uma rajada por posição digitada.
  useEffect(() => {
    const t = setTimeout(() => setTermo(local.trim()), 500);
    return () => clearTimeout(t);
  }, [local]);

  const moradores = useQuery({
    queryKey: ['moradores-da-posicao', termo],
    queryFn: () => quemMoraNaPosicao(termo),
    enabled: termo.length >= 2,
  });

  const liberar = useMutation({
    mutationFn: (productId: string) => limparPosicao(productId),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['moradores-da-posicao'] }),
  });

  const outros = (moradores.data ?? []).filter((m) => m.product_id !== ignorarId);
  if (termo.length < 2 || outros.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1 border border-alerta/40 bg-alerta/5 p-2">
      <p className="text-xs font-bold text-alerta">Nesta posição hoje:</p>
      {outros.map((m) => (
        <div key={m.product_id} className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-xs text-mute">
            <span className="font-mono">{m.sku}</span> {m.descricao}
            {' · '}
            {m.quantidade > 0 ? `${qtd(m.quantidade)} un` : 'zerada'}
            {!m.ativo && ' · inativa'}
          </span>
          {podeGerenciarCatalogo(papel) && m.quantidade <= 0 && (
            <button type="button"
                    className="shrink-0 border border-edge px-2 py-1 text-xs font-bold hover:border-signal"
                    disabled={liberar.isPending}
                    onClick={() => liberar.mutate(m.product_id)}>
              liberar posição
            </button>
          )}
        </div>
      ))}
      {liberar.error ? <p className="text-xs text-erro">{mensagemDeErro(liberar.error)}</p> : null}
      <p className="text-xs text-dim">
        Dividir posição é normal — o aviso existe para ninguém procurar peça no lugar errado.
      </p>
    </div>
  );
}

/**
 * A aba Foto — a peça e, quando existe grupo, as similares dela.
 *
 * Duas vidas bem diferentes:
 *  - PEÇA NOVA: não há id ainda, então as fotos escolhidas ficam comprimidas
 *    na mão (com pré-visualização, até 8, a primeira como principal) e sobem
 *    em série junto com o salvar. Falha no upload não derruba o cadastro — a
 *    peça vale mais que a foto, e o aviso diz quantas subiram.
 *  - EDIÇÃO: grava direto pela RPC, e as SIMILARES aparecem embaixo com um
 *    envio próprio cada — porque similar é peça cadastrada, com foto própria
 *    (o Bosch não é a foto do NGK).
 *
 * A compressão acontece no aparelho (canvas): 3–8 MB de câmera viram ~150 KB,
 * e o EXIF — com o GPS de quem fotografou — morre antes de sair do celular.
 */
function AbaFoto({ productId, grupoSimilar, pendentes, onPendentes }: {
  productId: string | null;
  grupoSimilar: string | null;
  pendentes: ImagemComprimida[];
  onPendentes: (l: ImagemComprimida[]) => void;
}) {
  const [erro, setErro] = useState<string | null>(null);

  const acrescentarPendente = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    setErro(null);
    try {
      onPendentes([...pendentes, await comprimirParaJpeg(arquivo)]);
    } catch (e) {
      setErro(mensagemDeErro(e));
    }
  };

  // Peça nova: a galeria fica na mão (pedido do dono, 13/08/2026 — antes só a
  // edição aceitava mais de uma foto) e sobe inteira no salvar, nesta ordem.
  // A mesma cara da galeria de edição, sem banco: aqui "tornar principal" é
  // só reordenar a lista.
  if (!productId) {
    return (
      <div className="space-y-3">
        {pendentes.length > 0 && (
          <ul className="flex flex-wrap gap-3">
            {pendentes.map((foto, i) => (
              <li key={foto.previa} className="w-32 space-y-1">
                <div className="relative">
                  <img src={foto.previa} alt={`Foto ${i + 1}`}
                       className="h-32 w-32 border border-edge object-cover" />
                  {i === 0 && (
                    <span className="absolute left-0 top-0 rounded-br-md bg-ink/85 px-1.5 py-0.5 text-[0.625rem] font-bold uppercase text-paper">
                      principal
                    </span>
                  )}
                </div>
                <p className="text-[0.6875rem] text-dim">
                  {Math.round(foto.bytes / 1024)} KB · {foto.largura}×{foto.altura}
                </p>
                {i > 0 && (
                  <button type="button" className="block w-full border border-edge px-1 py-1 text-[0.6875rem] font-bold"
                          onClick={() => onPendentes([foto, ...pendentes.filter((_, j) => j !== i)])}>
                    tornar principal
                  </button>
                )}
                <button type="button" className="block w-full border border-edge px-1 py-1 text-[0.6875rem] font-bold text-erro"
                        onClick={() => onPendentes(pendentes.filter((_, j) => j !== i))}>
                  remover
                </button>
              </li>
            ))}
          </ul>
        )}

        {pendentes.length < 8 ? (
          <EscolherFoto rotulo={pendentes.length === 0 ? 'Escolher a foto da peça' : '+ Acrescentar foto'}
                        onArquivo={acrescentarPendente} />
        ) : (
          <p className="text-xs text-dim">8 fotos é o teto da galeria.</p>
        )}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <p className="text-xs text-dim">
          {pendentes.length === 0
            ? 'A foto é comprimida no aparelho antes de subir — pode tirar direto pela câmera.'
            : 'Sobem juntas no salvar da peça, nesta ordem — a principal é a que aparece na busca e na ficha.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FotoDeUmaPeca productId={productId} titulo="Foto desta peça" />
      {grupoSimilar && <FotosDasSimilares grupo={grupoSimilar} atualId={productId} />}
    </div>
  );
}

/** O par input-escondido + botão que abre câmera ou álbum no celular. */
function EscolherFoto({ rotulo, onArquivo }: {
  rotulo: string;
  onArquivo: (f: File | undefined) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="botao-secundario inline-block cursor-pointer">
      {rotulo}
      <input id={id} type="file" accept="image/*" className="hidden"
             onChange={(e) => { onArquivo(e.target.files?.[0]); e.target.value = ''; }} />
    </label>
  );
}

/**
 * A galeria de UMA peça já cadastrada (031): a primeira foto é a PRINCIPAL —
 * é a que a ficha, as miniaturas e a busca mostram; as outras aparecem ao
 * abrir a foto na ficha e passar para o lado. Até 8 por peça.
 */
function FotoDeUmaPeca({ productId, titulo }: { productId: string; titulo: string }) {
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const galeria = useQuery({
    queryKey: ['fotos', productId],
    queryFn: () => fotosDaPeca(productId),
  });
  const fotos = galeria.data ?? [];

  const aoMudar = () => {
    setErro(null);
    void cliente.invalidateQueries({ queryKey: ['fotos'] });
  };
  const falhou = (e: unknown) => setErro(mensagemDeErro(e));

  const acrescentar = useMutation({
    mutationFn: async (arquivo: File) => {
      const img = await comprimirParaJpeg(arquivo);
      await adicionarFotoPeca({
        productId, base64: img.base64, largura: img.largura, altura: img.altura,
      });
    },
    onSuccess: aoMudar,
    onError: falhou,
  });
  const promover = useMutation({
    mutationFn: (fotoId: string) => definirFotoPrincipal(fotoId),
    onSuccess: aoMudar, onError: falhou,
  });
  const remover = useMutation({
    mutationFn: (fotoId: string) => removerFoto(fotoId),
    onSuccess: aoMudar, onError: falhou,
  });

  return (
    <div>
      {titulo && <p className="rotulo">{titulo}</p>}
      {fotos.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-3">
          {fotos.map((foto, i) => (
            <li key={foto.id} className="w-32 space-y-1">
              <div className="relative">
                <img src={urlDaFoto(foto.id, foto.updated_at)} alt={`Foto ${i + 1}`}
                     className="h-32 w-32 border border-edge object-cover" />
                {i === 0 && (
                  <span className="absolute left-0 top-0 rounded-br-md bg-ink/85 px-1.5 py-0.5 text-[0.625rem] font-bold uppercase text-paper">
                    principal
                  </span>
                )}
              </div>
              {/* A principal é POSIÇÃO, não flag: "tornar principal" renumera a
                  galeria inteira no banco, numa transação. */}
              {i > 0 && (
                <button type="button" className="block w-full border border-edge px-1 py-1 text-[0.6875rem] font-bold"
                        disabled={promover.isPending}
                        onClick={() => promover.mutate(foto.id)}>
                  tornar principal
                </button>
              )}
              <button type="button" className="block w-full border border-edge px-1 py-1 text-[0.6875rem] font-bold text-erro"
                      disabled={remover.isPending}
                      onClick={() => remover.mutate(foto.id)}>
                remover
              </button>
            </li>
          ))}
        </ul>
      )}

      {fotos.length < 8 ? (
        <EscolherFoto rotulo={fotos.length === 0 ? 'Enviar foto' : '+ Acrescentar foto'}
                      onArquivo={(f) => { if (f) acrescentar.mutate(f); }} />
      ) : (
        <p className="text-xs text-dim">A peça já tem 8 fotos — o teto da galeria.</p>
      )}
      {acrescentar.isPending && <p className="mt-1 text-xs text-dim">Comprimindo e enviando…</p>}
      {erro && <div className="mt-2"><Aviso tipo="erro">{erro}</Aviso></div>}
    </div>
  );
}

/** As similares do grupo, cada uma com o próprio envio — similar É outra peça. */
function FotosDasSimilares({ grupo, atualId }: { grupo: string; atualId: string }) {
  const similares = useQuery({
    queryKey: ['similares', grupo, atualId],
    queryFn: () => similaresDaPeca(grupo, atualId),
  });

  const itens = similares.data ?? [];
  if (itens.length === 0) return null;

  return (
    <div className="space-y-3 border-t border-edge pt-3">
      <p className="rotulo">Fotos das similares</p>
      <p className="text-xs text-mute">
        Cada similar é uma peça cadastrada e tem a própria foto — a do Bosch não é a do NGK.
      </p>
      {itens.map((s) => (
        <div key={s.id} className="border border-edge p-2">
          <p className="mb-2 text-xs font-semibold">
            {s.marca ?? 'sem marca'}
            <span className="ml-2 font-mono text-dim">{s.sku}</span>
          </p>
          <FotoDeUmaPeca productId={s.id} titulo="" />
        </div>
      ))}
    </div>
  );
}

/**
 * Campo de característica de texto/número com VÁRIOS valores (036): os já
 * escolhidos viram chips removíveis; o input acrescenta com Enter ou no botão.
 * É o que acaba com o "1.0, 1.3" digitado num campo numérico — cada valor
 * entra separado, e o filtro da busca acha a peça por qualquer um deles.
 */
function CampoMultivalor({ nome, valores, numerico, onAlternar }: {
  nome: string;
  valores: string[];
  numerico: boolean;
  onAlternar: (v: string) => void;
}) {
  const [texto, setTexto] = useState('');
  const acrescentar = () => {
    const v = texto.trim();
    if (!v || valores.includes(v)) { setTexto(''); return; }
    onAlternar(v);
    setTexto('');
  };
  return (
    <div>
      <p className="rotulo">{nome}</p>
      {valores.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-2">
          {valores.map((v) => (
            <button key={v} type="button" onClick={() => onAlternar(v)}
                    className="border border-signal bg-signal/10 px-2 py-1 text-xs font-bold"
                    title="Toque para remover">
              {v} ×
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          className="campo py-1 text-sm"
          value={texto}
          inputMode={numerico ? 'decimal' : undefined}
          placeholder={valores.length ? 'Outro valor…' : ''}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); acrescentar(); } }}
        />
        <button type="button" className="botao-secundario shrink-0 px-2 py-1 text-xs"
                disabled={!texto.trim()} onClick={acrescentar}>
          adicionar
        </button>
      </div>
    </div>
  );
}
